import { NavLink } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { useGuestLiveUpdates } from '../lib/communications/useGuestLiveUpdates';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import { msg, TovCard, TovIcon } from '@tov-plus/ui';

export function SchedulePage() {
  const access = useGuestAccess();
  const liveUpdates = useGuestLiveUpdates(access);
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const scheduleQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.schedule(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.schedule('pending', 'pending'),
    queryFn: () =>
      guestApi.listTimelineSummary(readyOccasionSlug!, readyGuestToken!),
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
  });

  if (access.status !== 'ready' || scheduleQuery.isLoading) {
    return <StatePanel preset="loading" />;
  }
  if (scheduleQuery.isError) {
    return <StatePanel preset="loading" />;
  }
  const items = scheduleQuery.data ?? [];
  const resolvedLocaleCode =
    access.experience.localizationContext.resolvedLocaleCode;
  const occasionTimezone = access.experience.occasion.timezone;
  if (items.length === 0) {
    return (
      <StatePanel
        preset="empty"
        actions={
          <>
            <NavLink className="primary-button" to="/events">
              {msg('pages.help_page.pages_help_page_browse_events')}{' '}
            </NavLink>
            <NavLink className="secondary-link" to="/">
              {msg('general.navigation.return_to_overview')}{' '}
            </NavLink>
          </>
        }
      />
    );
  }
  const sortedItems = [...items].sort(
    (first, second) =>
      Date.parse(first.timelineItem.startsAt) -
        Date.parse(second.timelineItem.startsAt) ||
      first.timelineItem.sortOrder - second.timelineItem.sortOrder,
  );
  const communicationLinks = access.experience.recentCommunications.filter(
    (item) => item.deepLink === '/schedule' || item.event !== null,
  );
  const eventIdsWithUpdates = new Set(
    communicationLinks
      .map((item) => item.event?.id ?? null)
      .filter((value): value is string => value !== null),
  );
  const upcomingItems = sortedItems.filter(
    (item) => Date.parse(item.timelineItem.startsAt) >= Date.now(),
  );
  const eventDayItems = sortedItems.filter((item) => {
    const delta = Date.parse(item.timelineItem.startsAt) - Date.now();
    return delta >= -1000 * 60 * 60 * 12 && delta <= 1000 * 60 * 60 * 36;
  });
  const changedItems = sortedItems.filter(
    (item) =>
      liveUpdates.hasUnreadEventUpdate(item.event.id) ||
      eventIdsWithUpdates.has(item.event.id),
  );
  const timelineGroups = Array.from(
    sortedItems
      .reduce((groups, item) => {
        const existing = groups.get(item.event.id);
        if (existing) {
          existing.items.push(item);
          return groups;
        }
        groups.set(item.event.id, {
          event: item.event,
          items: [item],
        });
        return groups;
      }, new Map<string, { event: (typeof sortedItems)[number]['event']; items: typeof sortedItems }>())
      .values(),
  );
  return (
    <div className="page-grid">
      <section className="hero-card">
        <p className="page-eyebrow">{msg('general.modules.schedule.name')}</p>
        <h2>
          {msg(
            'pages.schedule_page.pages_schedule_page_guest_safe_schedule_summary',
          )}
        </h2>
        <p>
          {msg(
            'pages.schedule_page.pages_schedule_page_this_schedule_only_shows_summary_items_that_planners',
          )}{' '}
        </p>
        <div className="hero-card__stats">
          <article>
            <strong>{items.length}</strong>
            <span>
              {msg(
                'pages.schedule_page.pages_schedule_page_visible_schedule_item',
              )}
            </span>
          </article>
          <article>
            <strong>{upcomingItems.length}</strong>
            <span>
              {msg('pages.schedule_page.pages_schedule_page_upcoming_item')}
            </span>
          </article>
          <article>
            <strong>{communicationLinks.length}</strong>
            <span>
              {msg('pages.schedule_page.pages_schedule_page_recent_update_cue')}
            </span>
          </article>
          <article>
            <strong>{eventDayItems.length}</strong>
            <span>
              {msg('pages.schedule_page.pages_schedule_page_event_day_item')}
            </span>
          </article>
        </div>
        <div className="action-row">
          <NavLink className="primary-button" to="/today">
            {msg(
              'pages.schedule_page.pages_schedule_page_open_today_view',
            )}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/events">
            {msg('pages.schedule_page.pages_schedule_page_event_details')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/seating">
            {msg('general.modules.seating.name')}{' '}
          </NavLink>
        </div>
        {liveUpdates.unreadCount > 0 ? (
          <p>
            {liveUpdates.unreadCount}{' '}
            {msg('pages.events_page.pages_events_page_unread_planner_update')}{' '}
            {liveUpdates.unreadCount === 1 ? '' : 's'}{' '}
            {msg(
              'pages.schedule_page.pages_schedule_page_may_affect_your_guest_schedule',
            )}{' '}
          </p>
        ) : null}
        {eventDayItems.length > 0 ? (
          <p>
            {msg(
              'pages.schedule_page.pages_schedule_page_event_day_mode_is_active_for',
            )}{' '}
            {eventDayItems.length}{' '}
            {msg('pages.schedule_page.pages_schedule_page_timeline_item')}{' '}
            {eventDayItems.length === 1 ? '' : 's'}
            {msg(
              'pages.schedule_page.pages_schedule_page_recheck_messages_and_locations_before_you_travel',
            )}{' '}
          </p>
        ) : null}
      </section>

      <TovCard variant="content">
        <p className="page-eyebrow">
          {msg('pages.schedule_page.pages_schedule_page_recent_update_focus')}
        </p>
        {communicationLinks.length > 0 ? (
          <div className="detail-list">
            {communicationLinks.slice(0, 3).map((item) => (
              <article key={item.delivery.id}>
                <strong>
                  {item.message?.subjectText ??
                    item.event?.displayName ??
                    msg('pages.help_page.pages_help_page_occasion_update')}
                </strong>
                <span>
                  {item.event ? `${item.event.displayName} • ` : ''}
                  {item.delivery.status.replaceAll('_', ' ')}
                </span>
                <span>
                  {msg('components.shell.sent')}{' '}
                  {formatGuestDateTime(
                    item.delivery.queuedAt,
                    resolvedLocaleCode,
                    occasionTimezone,
                  )}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.schedule_page.pages_schedule_page_no_recent_message_is_currently_redirecting_you_back',
            )}{' '}
          </p>
        )}
        <div className="action-row">
          <NavLink className="secondary-link" to="/messages">
            {msg('pages.schedule_page.pages_schedule_page_open_messages')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/events">
            {msg(
              'pages.schedule_page.pages_schedule_page_check_event_details',
            )}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/locations">
            {msg(
              'pages.schedule_page.pages_schedule_page_open_travel_guidance',
            )}{' '}
          </NavLink>
        </div>
      </TovCard>

      <TovCard variant="content">
        <p className="page-eyebrow">
          {msg('pages.schedule_page.pages_schedule_page_what_changed')}
        </p>
        {changedItems.length > 0 ? (
          <div className="detail-list">
            {changedItems.slice(0, 4).map((item) => (
              <article key={`changed-${item.timelineItem.id}`}>
                <strong>{item.timelineItem.title}</strong>
                <span>{item.event.displayName}</span>
                <span>
                  {liveUpdates.hasUnreadEventUpdate(item.event.id)
                    ? msg(
                        'pages.schedule_page.pages_schedule_page_unread_planner_update_linked_to_this_event',
                      )
                    : msg(
                        'pages.schedule_page.pages_schedule_page_recent_planner_update_published_for_this_event',
                      )}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.schedule_page.pages_schedule_page_no_recent_planner_change_is_currently_pushing_you',
            )}{' '}
          </p>
        )}
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.schedule_page.pages_schedule_page_timeline_by_event')}
        </p>
        <div className="guest-schedule-timeline">
          {timelineGroups.map((group) => (
            <section
              key={group.event.id}
              className="guest-schedule-event"
              aria-label={group.event.displayName}
            >
              <div className="guest-schedule-event__header">
                <div>
                  <h3>{group.event.displayName}</h3>
                  <p>
                    {group.event.startsAt
                      ? formatGuestDateTime(
                          group.event.startsAt,
                          resolvedLocaleCode,
                          group.event.timezone ?? occasionTimezone,
                        )
                      : msg(
                          'pages.events_page.pages_events_page_time_not_published_yet',
                        )}
                  </p>
                </div>
                <div className="guest-schedule-event__actions">
                  <NavLink to={`/events/${group.event.id}`}>
                    <TovIcon name="arrow-right" />
                    {msg('pages.event_detail_page.event_details')}
                  </NavLink>
                  <NavLink to="/locations">
                    <TovIcon name="map-pin" />
                    {msg('pages.today_page.pages_today_page_travel_guidance')}
                  </NavLink>
                </div>
              </div>
              <ol className="guest-schedule-items">
                {group.items.map((item) => {
                  const hasItemUpdate =
                    liveUpdates.hasUnreadEventUpdate(item.event.id) ||
                    eventIdsWithUpdates.has(item.event.id);
                  const isEventDayItem = eventDayItems.some(
                    (candidate) =>
                      candidate.timelineItem.id === item.timelineItem.id,
                  );
                  return (
                    <li key={item.timelineItem.id}>
                      <time>
                        {formatGuestDateTime(
                          item.timelineItem.startsAt,
                          resolvedLocaleCode,
                          item.event.timezone ?? occasionTimezone,
                        )}
                      </time>
                      <div>
                        <strong>{item.timelineItem.title}</strong>
                        <p>
                          {item.timelineItem.description ??
                            item.event.guestSummary ??
                            msg(
                              'pages.today_page.pages_today_page_no_extra_guest_summary_provided',
                            )}
                        </p>
                        <div className="guest-schedule-item__meta">
                          {item.timelineItem.locationLabel ? (
                            <span>
                              <TovIcon name="map-pin" />
                              {item.timelineItem.locationLabel}
                            </span>
                          ) : null}
                          {item.timelineItem.durationMinutes ? (
                            <span>
                              <TovIcon name="clock" />
                              {item.timelineItem.durationMinutes}{' '}
                              {msg(
                                'pages.schedule_page.pages_schedule_page_min',
                              )}
                            </span>
                          ) : null}
                          {hasItemUpdate ? (
                            <NavLink to="/messages">
                              <TovIcon name="megaphone" />
                              {msg(
                                'pages.schedule_page.pages_schedule_page_update',
                              )}
                            </NavLink>
                          ) : null}
                          {isEventDayItem ? (
                            <span>
                              <TovIcon name="calendar-blank" />
                              {msg('general.calendar.today')}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
        </div>
      </TovCard>
    </div>
  );
}
